import {
  Button,
  Card,
  CardActions,
  CardContent,
  Flex,
  FlexItem,
  Icon,
  Image,
  Text,
} from '@chatui/core';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { MultiMediaReponseType } from '../../../utils/types';
import './style.css';
import { useCallback, useMemo, useRef, useState } from 'react';
import { uploadFile } from '../../../services/api';

interface UploadCardProps {
  upload: MultiMediaReponseType;
  actions: { onSuccess: (msg: string) => void; onError: () => void };
  fileType: string;
}
const UploadCard = ({ upload, actions, fileType }: UploadCardProps) => {
  const { data } = upload;
  const [fileName, setFileName] = useState<string | null>(null);
  const { fields, url, s3_media_url } = data;
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);

  const handleBrowseClick = useCallback(() => {
    if (uploading || uploadSuccess) return;
    fileInputRef.current?.click();
  }, [uploadSuccess, uploading]);

  const handleFile = useCallback((file: File | undefined) => {
    if (file) {
      setFile(file);
      const reader = new FileReader();
      if (file.type.startsWith('image/')) {
        reader.onload = (e) => setPreviewUrl(e.target?.result as string);
      } else {
        setFileName(file.name);
      }
      reader.readAsDataURL(file);
    }
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      handleFile(file);
    },
    [handleFile]
  );

  const handleUpload = useCallback(async () => {
    if (file) {
      setUploading(true);
      const formData = new FormData();
      formData.append('key', fields.key);
      formData.append('AWSAccessKeyId', fields.AWSAccessKeyId);
      formData.append('x-amz-security-token', fields['x-amz-security-token']);
      formData.append('policy', fields.policy);
      formData.append('signature', fields.signature);
      formData.append('file', file);
      try {
        await uploadFile(url, formData);
        setUploadSuccess(true);
        actions.onSuccess(s3_media_url);
      } catch (e) {
        console.log(e);
        actions.onError();
      } finally {
        setUploading(false);
      }
    }
  }, [actions, fields, file, url]);

  const acceptTypes = useMemo(() => {
    if (fileType === 'image') return 'image/*';
    if (fileType === 'document')
      return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt';
    return '';
  }, [fileType]);

  const preview = useMemo(() => {
    if (!previewUrl && !fileName) {
      return <FaCloudUploadAlt size={50} color="#999" cursor={'pointer'} />;
    }
    if (previewUrl) {
      return <Image src={previewUrl} />;
    }
    return <Text className="file-name">{fileName}</Text>;
  }, [fileName, previewUrl]);

  return (
    <Card className="upload-card">
      <CardContent>
        <div onClick={handleBrowseClick} style={{ cursor: 'pointer' }}>
          <Flex center>{preview}</Flex>
          <Flex center>
            <Button variant="text" size="sm">
              Click to choose a file
            </Button>
          </Flex>
        </div>
      </CardContent>
      <Flex className="file-upload-wrapper">
        <FlexItem style={{ flex: 'none' }}>
          <input
            type="file"
            className="file-input"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={acceptTypes}
          />
        </FlexItem>
      </Flex>
      {file ? (
        <CardActions>
          <Button
            color="primary"
            onClick={handleUpload}
            disabled={uploading || uploadSuccess}
          >
            {uploading ? (
              <Icon type="spinner" spin fontSize="1.5em" />
            ) : uploadSuccess ? (
              'Uploaded'
            ) : (
              'Upload'
            )}
          </Button>
        </CardActions>
      ) : null}
    </Card>
  );
};
export default UploadCard;
